import { useEffect, useRef, useState } from 'react';
import { parseColor, toHex, type Backdrop, type Rgb } from '@eip/engine';
import { addScene, removeScene, sceneStoreKind } from '../app/session';
import { SCENES_SESSION_ONLY_NOTICE, type SceneSource } from '../lib/scenes';
import {
  BACKDROP_NOT_SEPARABLE_HINT, backdropKind, nextPendingKind, normalizeHex, shownBackdropKind, withBackdropKind, type BackdropKind,
} from '../lib/mockup-ui';
import { nextDraft } from './controls';
import { Section } from './Section';

const KIND_OPTIONS: Array<{ value: BackdropKind; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'color', label: 'Colour' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'scene', label: 'Scene' },
];

/**
 * One hex field: a colour picker and the hex beside it, both writing the same `Rgb`.
 *
 * The text field follows the panel's draft rule (`nextDraft`, commit on blur/Enter, Escape to
 * revert): a half-typed `#3a6` must not reach the job, and something that is not a colour at all
 * keeps the last value rather than clearing the backdrop. The picker commits on change — that is
 * what a native colour input is for, and the render debounce is what makes dragging it affordable.
 *
 * The `aria-label`s: the text input takes `label` itself (it is the one a test or a screen reader
 * addresses by name — "Backdrop colour", "Top", "Bottom") and the picker takes "<label> picker", so
 * the two controls of one row are never the same name. Chromium exposes a colour input as a
 * `textbox` as well, so a test addressing the hex field by role needs `exact` — as the panel's
 * "Width" field already does against "Width (sleeve)".
 */
function HexField({ label, value, disabled, onCommit }: {
  label: string;
  value: string;
  disabled: boolean;
  onCommit: (rgb: Rgb) => void;
}) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  const dirty = useRef(false);
  useEffect(() => { setDraft((prev) => nextDraft(prev, value, focused.current)); }, [value]);

  const commit = (raw: string) => {
    const hex = normalizeHex(raw);
    // Invalid keeps the committed value, shown back in the field so what is on screen is what the
    // job holds — the same contract the placement fields' `commitDraft` has.
    setDraft(hex ?? value);
    if (hex && hex !== value) onCommit(parseColor(hex));
  };

  return (
    <label className="ctl">
      <span className="ctl-label">{label}</span>
      <input
        type="color" value={value} disabled={disabled} aria-label={`${label} picker`}
        onChange={(e) => { const hex = normalizeHex(e.target.value); if (hex) onCommit(parseColor(hex)); }}
      />
      <input
        value={draft} aria-label={label} aria-invalid={!normalizeHex(draft)} disabled={disabled} style={{ width: 84 }}
        onFocus={() => { focused.current = true; }}
        onChange={(e) => { setDraft(e.target.value); dirty.current = true; }}
        onBlur={() => { focused.current = false; if (dirty.current) { commit(draft); dirty.current = false; } }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { commit(draft); dirty.current = false; }
          else if (e.key === 'Escape') { setDraft(value); dirty.current = false; }
        }}
      />
    </label>
  );
}

/**
 * The "Backdrop" section: what the garment is photographed AGAINST, swapped for a colour, a
 * vertical gradient or one of the shop's own scene photographs (with the drop shadow the engine
 * puts under the garment either way). Owns its `.section`/`<h3>` the way `ColorSwatches` does, so
 * `MockupPanel` mounts it with no extra wrapper.
 *
 * Every write goes out through `onChange` as a whole `Backdrop | null` — the panel folds it into the
 * job and the store sanitises it (a scene the list no longer holds clears itself there).
 *
 * `separable === false` is a blank whose mask could not be told apart from its own backdrop (the
 * on-model built-in): the controls are disabled and say why, because the worker would ignore the
 * backdrop anyway. `undefined` — a template still preparing, or one with no status yet — leaves
 * them live: a swap made now simply takes effect when the maps land, and disabling a section for
 * the second a preparation takes would flicker on every template switch.
 */
export function BackdropControls({ backdrop, scenes, separable, onChange }: {
  backdrop: Backdrop | null;
  scenes: ReadonlyArray<SceneSource>;
  separable: boolean | undefined;
  onChange: (backdrop: Backdrop | null) => void;
}) {
  /** A refused import (too large, or not an image the browser can decode). Cleared by the next one
   *  that lands, so the line describes the file the shop is looking at. */
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  /**
   * A kind the shop has picked that the JOB cannot carry: "Scene" with nothing imported yet writes
   * no backdrop at all (there is no scene to name), and the Scene row still has to appear or the
   * "Add scene…" button behind it is unreachable. The reconciliation is `nextPendingKind` /
   * `shownBackdropKind` — pure and tested, because getting it wrong here is invisible to this suite.
   */
  const [pendingKind, setPendingKind] = useState<BackdropKind | null>(null);
  const jobKind = backdropKind(backdrop);
  // Spent once the job HAS a kind (an import, or another kind chosen): dropping the state then keeps
  // it from being resurrected later — deleting the last scene reads as None, not as a Scene row the
  // shop never asked for again. `shownBackdropKind` ignores it in that window regardless, so this
  // effect is tidiness rather than the rule.
  useEffect(() => { if (jobKind !== 'none') setPendingKind(null); }, [jobKind]);
  const kind = shownBackdropKind(jobKind, pendingKind);

  const disabled = separable === false;
  /** IndexedDB refused to open: whatever is imported lasts for this session. */
  const sessionOnly = sceneStoreKind() === 'memory';
  const sceneId = backdrop?.kind === 'scene' ? backdrop.sceneId : '';
  const selected = scenes.find((s) => s.id === sceneId);

  const onKindChange = (next: BackdropKind) => {
    setPendingKind(nextPendingKind(next, scenes));
    onChange(withBackdropKind(backdrop, next, scenes));
  };

  const onFile = async (file: File) => {
    setError(null);
    try {
      const scene = await addScene(file);
      // Straight onto the job: importing a backdrop is asking for it, and the alternative is a
      // scene in the list that the shop then has to select as a second step.
      onChange({ kind: 'scene', sceneId: scene.id });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Section id="backdrop" title="Backdrop">
      <label className="ctl">
        <span className="ctl-label">Backdrop</span>
        <select aria-label="Backdrop" value={kind} disabled={disabled} onChange={(e) => onKindChange(e.target.value as BackdropKind)}>
          {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>

      {kind === 'color' && backdrop?.kind === 'color' && (
        <HexField label="Backdrop colour" value={toHex(backdrop.rgb)} disabled={disabled} onCommit={(rgb) => onChange({ kind: 'color', rgb })} />
      )}

      {kind === 'gradient' && backdrop?.kind === 'gradient' && (
        <>
          <HexField label="Top" value={toHex(backdrop.top)} disabled={disabled} onCommit={(top) => onChange({ kind: 'gradient', top, bottom: backdrop.bottom })} />
          <HexField label="Bottom" value={toHex(backdrop.bottom)} disabled={disabled} onCommit={(bottom) => onChange({ kind: 'gradient', top: backdrop.top, bottom })} />
        </>
      )}

      {kind === 'scene' && (
        <>
          <label className="ctl">
            <span className="ctl-label">Scene</span>
            <select
              aria-label="Scene" value={sceneId} disabled={disabled || scenes.length === 0}
              onChange={(e) => onChange(e.target.value ? { kind: 'scene', sceneId: e.target.value } : null)}
            >
              {/* Only while there is nothing selected: with scenes imported and none chosen the
                  select must not read as the first of them. */}
              {!selected && <option value="">{scenes.length ? 'Choose a scene' : 'No scenes yet'}</option>}
              {scenes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <div className="row">
            {/* The input is hidden and clicked through this button: a bare file input reads as a
                browser control in the middle of a panel of app controls, and the editor's own photo
                row is the one place the raw one is wanted. */}
            <input
              ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" aria-label="Scene image" hidden disabled={disabled}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first, so re-picking the same file after a refusal fires `change` again.
                e.target.value = '';
                if (file) void onFile(file);
              }}
            />
            <button type="button" disabled={disabled} onClick={() => fileInput.current?.click()}>Add scene…</button>
            {/* No confirmation: it is the shop's own upload, and a job using it falls back to the
                photo's own backdrop (`removeScene` clears every one of them). */}
            <button
              type="button" disabled={disabled || !selected}
              title={selected ? undefined : 'Select a scene first'}
              onClick={() => { if (selected) { setError(null); void removeScene(selected.id).catch((e: Error) => setError(e.message)); } }}
            >
              Remove scene
            </button>
          </div>
          {sessionOnly && <div className="muted small">{SCENES_SESSION_ONLY_NOTICE}</div>}
          {error && <div className="error small">{error}</div>}
        </>
      )}

      {disabled && <div className="muted small">{BACKDROP_NOT_SEPARABLE_HINT}</div>}
    </Section>
  );
}
